import { useEffect, useRef, useCallback } from 'react';
import * as d3 from 'd3';
import type { Hotspot } from '../../../data/notationHotspots';
import { layerInfo } from '../../../data/notationHotspots';

interface SystemGraphProps {
  hotspots: Hotspot[];
  selectedId: string | null;
  exploredIds: Set<string>;
  onSelectNode: (id: string) => void;
}

interface GraphNode {
  id: string;
  label: string;
  layer: string;
  stage: number;
  x?: number;
  y?: number;
  fx?: number | null;
  fy?: number | null;
}

interface GraphLink {
  source: string | GraphNode;
  target: string | GraphNode;
}

const stageColor = (layer: string) => {
  return layerInfo[layer as keyof typeof layerInfo]?.color || '#8a8fa3';
};

export function SystemGraph({ hotspots, selectedId, exploredIds, onSelectNode }: SystemGraphProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const graphRef = useRef<{
    nodes: GraphNode[];
    links: GraphLink[];
    node: d3.Selection<any, GraphNode, any, unknown>;
    link: d3.Selection<any, GraphLink, any, unknown>;
  } | null>(null);
  const onSelectNodeRef = useRef(onSelectNode);
  onSelectNodeRef.current = onSelectNode;

  // Build the force graph once
  useEffect(() => {
    if (!svgRef.current || !containerRef.current) return;

    const width = containerRef.current.clientWidth || 700;
    const height = containerRef.current.clientHeight || 450;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    svg.attr('width', width).attr('height', height);

    const nodes: GraphNode[] = hotspots.map(h => ({
      id: h.id,
      label: h.label,
      layer: h.layer,
      stage: h.stage,
    }));

    const nodeIds = new Set(nodes.map(n => n.id));
    const links: GraphLink[] = [];
    hotspots.forEach(h => {
      h.relatedIds?.forEach(rid => {
        if (nodeIds.has(rid)) {
          links.push({ source: h.id, target: rid });
        }
      });
    });

    // Defs
    const defs = svg.append('defs');
    defs.append('marker')
      .attr('id', 'graph-arrow')
      .attr('viewBox', '0 0 10 10')
      .attr('refX', 20)
      .attr('refY', 5)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M 0 0 L 10 5 L 0 10 z')
      .attr('fill', '#999')
      .attr('opacity', 0.4);

    // Force simulation
    const simulation = d3.forceSimulation(nodes as d3.SimulationNodeDatum[])
      .force('link', d3.forceLink(links).id((d: any) => d.id).distance(80).strength(0.3))
      .force('charge', d3.forceManyBody().strength(-200))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(25))
      .force('x', d3.forceX(width / 2).strength(0.05))
      .force('y', d3.forceY().y((d: any) => {
        const stage = d.stage;
        return (stage / 4) * height * 0.7 + height * 0.15;
      }).strength(0.15));

    // Links
    const linkGroup = svg.append('g').attr('class', 'links');
    const link = linkGroup.selectAll('line')
      .data(links)
      .join('line')
      .attr('stroke', '#999')
      .attr('stroke-opacity', 0.15)
      .attr('stroke-width', 1)
      .attr('marker-end', 'url(#graph-arrow)');

    // Nodes
    const nodeGroup = svg.append('g').attr('class', 'nodes');
    const node = nodeGroup.selectAll('g')
      .data(nodes)
      .join('g')
      .attr('cursor', 'pointer')
      .on('click', (_event: any, d: GraphNode) => {
        onSelectNodeRef.current(d.id);
      })
      .call(d3.drag<any, GraphNode>()
        .on('start', (event: any, d: any) => {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event: any, d: any) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event: any, d: any) => {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        })
      );

    // Node circles
    node.append('circle')
      .attr('r', (d: GraphNode) => exploredIds.has(d.id) ? 12 : 9)
      .attr('fill', (d: GraphNode) => stageColor(d.layer))
      .attr('stroke', 'white')
      .attr('stroke-width', 2)
      .attr('opacity', (d: GraphNode) => exploredIds.has(d.id) ? 1 : 0.5);

    // Node labels
    node.append('text')
      .text((d: GraphNode) => d.label.length > 12 ? d.label.slice(0, 11) + '...' : d.label)
      .attr('x', 16)
      .attr('y', 4)
      .attr('font-size', '9px')
      .attr('fill', 'var(--color-text-muted)')
      .attr('pointer-events', 'none');

    // Tick handler
    simulation.on('tick', () => {
      link
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y);

      node.attr('transform', (d: any) => `translate(${d.x},${d.y})`);
    });

    graphRef.current = { nodes, links, node, link };

    return () => {
      simulation.stop();
      graphRef.current = null;
    };
  }, [hotspots]); // Only rebuild graph when hotspots change

  // Update highlights separately — no graph rebuild
  const updateHighlight = useCallback(() => {
    const graph = graphRef.current;
    if (!graph) return;
    const { nodes, links, node, link } = graph;

    if (!selectedId) {
      node.select('circle')
        .transition().duration(300)
        .attr('opacity', (d: GraphNode) => exploredIds.has(d.id) ? 1 : 0.5)
        .attr('r', (d: GraphNode) => exploredIds.has(d.id) ? 12 : 9);
      node.select('text').transition().duration(300).attr('opacity', 1);
      link.transition().duration(300)
        .attr('stroke-opacity', 0.15).attr('stroke', '#999').attr('stroke-width', 1);
      return;
    }

    const connectedIds = new Set<string>();
    connectedIds.add(selectedId);
    links.forEach(l => {
      const sid = typeof l.source === 'string' ? l.source : (l.source as GraphNode).id;
      const tid = typeof l.target === 'string' ? l.target : (l.target as GraphNode).id;
      if (sid === selectedId) connectedIds.add(tid);
      if (tid === selectedId) connectedIds.add(sid);
    });

    node.select('circle')
      .transition().duration(300)
      .attr('opacity', (d: GraphNode) => connectedIds.has(d.id) ? 1 : 0.15)
      .attr('r', (d: GraphNode) => d.id === selectedId ? 16 : connectedIds.has(d.id) ? 12 : 8);

    node.select('text')
      .transition().duration(300)
      .attr('opacity', (d: GraphNode) => connectedIds.has(d.id) ? 1 : 0.2);

    link
      .transition().duration(300)
      .attr('stroke-opacity', (d: any) => {
        const sid = typeof d.source === 'string' ? d.source : d.source.id;
        const tid = typeof d.target === 'string' ? d.target : d.target.id;
        return (sid === selectedId || tid === selectedId) ? 0.7 : 0.05;
      })
      .attr('stroke', (d: any) => {
        const sid = typeof d.source === 'string' ? d.source : d.source.id;
        const tid = typeof d.target === 'string' ? d.target : d.target.id;
        if (sid === selectedId || tid === selectedId) {
          const sourceNode = nodes.find(n => n.id === sid);
          return stageColor(sourceNode?.layer || 'sensory');
        }
        return '#999';
      })
      .attr('stroke-width', (d: any) => {
        const sid = typeof d.source === 'string' ? d.source : d.source.id;
        const tid = typeof d.target === 'string' ? d.target : d.target.id;
        return (sid === selectedId || tid === selectedId) ? 2.5 : 1;
      });
  }, [selectedId, exploredIds]);

  useEffect(() => {
    updateHighlight();
  }, [updateHighlight]);

  return (
    <div className="system-graph" ref={containerRef} style={{ width: '100%', height: '100%' }}>
      <svg ref={svgRef}></svg>
    </div>
  );
}
